import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaFileContract, FaGavel, FaUserFriends, FaSave, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import DefendantSearch from '../components/DefendantSearch';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ChargeSelector from '../components/ChargeSelector';
import './NewBond.css';

const NewBond = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [courts, setCourts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Defendant selection state
  const [existingDefendantId, setExistingDefendantId] = useState(null);
  const [isExistingDefendant, setIsExistingDefendant] = useState(false);

  // Form data state
  const [availablePowers, setAvailablePowers] = useState([]);
  const [loadingPowers, setLoadingPowers] = useState(false);

  const [formData, setFormData] = useState({
    // Defendant Information
    defendant: {
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      ssn: '',
      phonePrimary: '',
      phoneSecondary: '',
      email: '',
      addressStreet: '',
      addressCity: '',
      addressState: 'MT',
      addressZip: '',
      height: '',
      weight: '',
      hairColor: '',
      eyeColor: '',
      gender: '',
      employer: '',
      occupation: ''
    },
    // Bond Information
    bond: {
      bondAmount: '',
      premiumAmount: '',
      premiumPaid: '',
      powerId: '',
      collateralType: '',
      otherCollateralType: '',
      collateralDescription: ''
    },
    // Court/Case Information
    court: {
      courtId: '',
      caseNumber: '',
      selectedCharges: [],
      arrestDate: '',
      bookingNumber: '',
      nextCourtDate: '',
      courtTime: '',
      judgeName: '',
      hearingType: 'arraignment'
    },
    // Cosigner/Indemnitor Information
    cosigner: {
      firstName: '',
      lastName: '',
      relationship: '',
      phone: '',
      email: '',
      addressStreet: '',
      addressCity: '',
      addressState: 'MT',
      addressZip: ''
    },
    // Agent Assignment
    agentId: ''
  });

  useEffect(() => {
    fetchCourts();
    fetchAgents();
  }, []);

  const fetchCourts = async () => {
    try {
      const response = await axios.get('/court-dates/courts');
      if (response.data.success) {
        setCourts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching courts:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/users', {
        params: { role: 'agent' }
      });
      if (response.data.success) {
        setAgents(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleAgentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      agentId: value
    }));
  };

  const handleDefendantSelect = (selection) => {
    setIsExistingDefendant(selection.isExisting);
    setExistingDefendantId(selection.defendantId);

    // Update form data with selected defendant data
    setFormData(prev => ({
      ...prev,
      defendant: selection.data
    }));
  };

  const handleBondAmountChange = async (value) => {
    const amount = parseFloat(value) || 0;

    // Calculate premium: 10% of bond amount, minimum $100 if amount <= $1000
    let premium = amount * 0.10;
    if (amount > 0 && amount <= 1000) {
      premium = 100;
    }

    // Update bond amount and premium
    setFormData(prev => ({
      ...prev,
      bond: {
        ...prev.bond,
        bondAmount: value,
        premiumAmount: premium.toFixed(2),
        powerId: '' // Reset power selection when amount changes
      }
    }));

    // Fetch available powers for this bond amount
    if (amount > 0) {
      setLoadingPowers(true);
      try {
        const response = await axios.get(`/powers/available`, {
          params: { bondAmount: amount }
        });
        if (response.data.success) {
          setAvailablePowers(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching available powers:', error);
        setAvailablePowers([]);
      } finally {
        setLoadingPowers(false);
      }
    } else {
      setAvailablePowers([]);
    }
  };

  const handleDefendantAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      defendant: {
        ...prev.defendant,
        addressStreet: addressData.street || '',
        addressCity: addressData.city || '',
        addressState: addressData.state || 'MT',
        addressZip: addressData.zip || ''
      }
    }));
  };

  const handleCosignerAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      cosigner: {
        ...prev.cosigner,
        addressStreet: addressData.street || '',
        addressCity: addressData.city || '',
        addressState: addressData.state || 'MT',
        addressZip: addressData.zip || ''
      }
    }));
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      let defendantId;

      // Use existing defendant or create new one
      if (isExistingDefendant && existingDefendantId) {
        defendantId = existingDefendantId;
      } else {
        // Submit defendant first
        const defendantData = {
          first_name: formData.defendant.firstName,
          middle_name: formData.defendant.middleName,
          last_name: formData.defendant.lastName,
          date_of_birth: formData.defendant.dateOfBirth,
          ssn: formData.defendant.ssn,
          phone_primary: formData.defendant.phonePrimary,
          phone_secondary: formData.defendant.phoneSecondary,
          email: formData.defendant.email,
          address_street: formData.defendant.addressStreet,
          address_city: formData.defendant.addressCity,
          address_state: formData.defendant.addressState,
          address_zip: formData.defendant.addressZip,
          height: formData.defendant.height,
          weight: formData.defendant.weight ? parseInt(formData.defendant.weight) : null,
          hair_color: formData.defendant.hairColor,
          eye_color: formData.defendant.eyeColor,
          gender: formData.defendant.gender,
          employer: formData.defendant.employer,
          occupation: formData.defendant.occupation
        };

        const defendantResponse = await axios.post(
          '/defendants',
          defendantData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        defendantId = defendantResponse.data.data.id;
      }

      // Submit bond
      const bondData = {
        defendant_id: defendantId,
        power_id: formData.bond.powerId || null,
        bond_amount: parseFloat(formData.bond.bondAmount),
        premium_amount: parseFloat(formData.bond.premiumAmount),
        premium_paid: parseFloat(formData.bond.premiumPaid) || 0,
        collateral_type: formData.bond.collateralType,
        other_collateral_type: formData.bond.otherCollateralType || null,
        status: 'active',
        collateral_description: formData.bond.collateralDescription,
        agent_id: formData.agentId || null
      };

      const bondResponse = await axios.post(
        '/bonds',
        bondData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const bondId = bondResponse.data.data.id;

      // Submit charges to bond_charges table
      if (formData.court.selectedCharges.length > 0) {
        for (const charge of formData.court.selectedCharges) {
          try {
            await axios.post(
              `/bonds/${bondId}/charges`,
              {
                charge_id: charge.chargeId,
                count: charge.count
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (chargeError) {
            console.error('Error saving charge:', chargeError);
            // Continue with other charges even if one fails
          }
        }
      }

      // Submit court date if provided
      if (formData.court.nextCourtDate) {
        // Create charges summary string for court date
        const chargesSummary = formData.court.selectedCharges
          .map(c => `${c.count > 1 ? c.count + ' counts of ' : ''}${c.mcaCode} - ${c.description}`)
          .join('; ');

        const courtData = {
          defendant_id: defendantId,
          bond_id: bondId,
          court_id: formData.court.courtId || null,
          case_number: formData.court.caseNumber,
          scheduled_date: formData.court.nextCourtDate,
          scheduled_time: formData.court.courtTime,
          hearing_type: formData.court.hearingType,
          judge_name: formData.court.judgeName,
          charges: chargesSummary || ''
        };

        await axios.post(
          '/court-dates',
          courtData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Submit cosigner if provided
      if (formData.cosigner.firstName && formData.cosigner.lastName) {
        const cosignerData = {
          defendant_id: defendantId,
          first_name: formData.cosigner.firstName,
          last_name: formData.cosigner.lastName,
          relationship: formData.cosigner.relationship,
          phone: formData.cosigner.phone,
          email: formData.cosigner.email,
          address_street: formData.cosigner.addressStreet,
          address_city: formData.cosigner.addressCity,
          address_state: formData.cosigner.addressState,
          address_zip: formData.cosigner.addressZip
        };

        await axios.post(
          '/defendants/' + defendantId + '/cosigners',
          cosignerData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      alert('Bond created successfully!');
      navigate('/bonds');
    } catch (error) {
      console.error('Error creating bond:', error);
      alert('Error creating bond: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
        <div className="step-number">1</div>
        <div className="step-label">Defendant Info</div>
      </div>
      <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
        <div className="step-number">2</div>
        <div className="step-label">Bond Details</div>
      </div>
      <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
        <div className="step-number">3</div>
        <div className="step-label">Court Info</div>
      </div>
      <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
        <div className="step-number">4</div>
        <div className="step-label">Cosigner</div>
      </div>
    </div>
  );

  const renderDefendantForm = () => (
    <div className="form-section">
      <h2><FaUser /> Defendant Information</h2>

      <DefendantSearch onDefendantSelect={handleDefendantSelect} />

      {isExistingDefendant && (
        <div style={{
          padding: '12px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '6px',
          marginBottom: '1rem',
          color: '#155724'
        }}>
          <strong>Note:</strong> You are creating a bond for an existing defendant. Their information is shown below and can be edited if needed.
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            value={formData.defendant.firstName}
            onChange={(e) => handleInputChange('defendant', 'firstName', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Middle Name</label>
          <input
            type="text"
            value={formData.defendant.middleName}
            onChange={(e) => handleInputChange('defendant', 'middleName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            value={formData.defendant.lastName}
            onChange={(e) => handleInputChange('defendant', 'lastName', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Date of Birth *</label>
          <input
            type="date"
            value={formData.defendant.dateOfBirth}
            onChange={(e) => handleInputChange('defendant', 'dateOfBirth', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>SSN</label>
          <input
            type="text"
            placeholder="###-##-####"
            value={formData.defendant.ssn}
            onChange={(e) => handleInputChange('defendant', 'ssn', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Gender</label>
          <select
            value={formData.defendant.gender}
            onChange={(e) => handleInputChange('defendant', 'gender', e.target.value)}
          >
            <option value="">Select...</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="O">Other</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Primary Phone *</label>
          <input
            type="tel"
            value={formData.defendant.phonePrimary}
            onChange={(e) => handleInputChange('defendant', 'phonePrimary', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Secondary Phone</label>
          <input
            type="tel"
            value={formData.defendant.phoneSecondary}
            onChange={(e) => handleInputChange('defendant', 'phoneSecondary', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={formData.defendant.email}
            onChange={(e) => handleInputChange('defendant', 'email', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Street Address *</label>
        <AddressAutocomplete
          value={formData.defendant.addressStreet}
          onChange={(value) => handleInputChange('defendant', 'addressStreet', value)}
          onAddressSelect={handleDefendantAddressSelect}
          placeholder="Start typing address..."
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City *</label>
          <input
            type="text"
            value={formData.defendant.addressCity}
            onChange={(e) => handleInputChange('defendant', 'addressCity', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>State *</label>
          <input
            type="text"
            maxLength="2"
            value={formData.defendant.addressState}
            onChange={(e) => handleInputChange('defendant', 'addressState', e.target.value.toUpperCase())}
            required
          />
        </div>
        <div className="form-group">
          <label>ZIP Code *</label>
          <input
            type="text"
            maxLength="10"
            value={formData.defendant.addressZip}
            onChange={(e) => handleInputChange('defendant', 'addressZip', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Height</label>
          <input
            type="text"
            placeholder="e.g., 5'10&quot;"
            value={formData.defendant.height}
            onChange={(e) => handleInputChange('defendant', 'height', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Weight (lbs)</label>
          <input
            type="number"
            value={formData.defendant.weight}
            onChange={(e) => handleInputChange('defendant', 'weight', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Hair Color</label>
          <input
            type="text"
            value={formData.defendant.hairColor}
            onChange={(e) => handleInputChange('defendant', 'hairColor', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Eye Color</label>
          <input
            type="text"
            value={formData.defendant.eyeColor}
            onChange={(e) => handleInputChange('defendant', 'eyeColor', e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Employer</label>
          <input
            type="text"
            value={formData.defendant.employer}
            onChange={(e) => handleInputChange('defendant', 'employer', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Occupation</label>
          <input
            type="text"
            value={formData.defendant.occupation}
            onChange={(e) => handleInputChange('defendant', 'occupation', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderBondForm = () => (
    <div className="form-section">
      <h2><FaFileContract /> Bond Details</h2>

      <div className="form-row">
        <div className="form-group">
          <label>Bond Amount *</label>
          <input
            type="number"
            step="0.01"
            placeholder="Enter bond amount"
            value={formData.bond.bondAmount}
            onChange={(e) => handleBondAmountChange(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Premium Amount (Auto-calculated) *</label>
          <input
            type="number"
            step="0.01"
            value={formData.bond.premiumAmount}
            readOnly
            style={{ background: '#f5f5f5' }}
            required
          />
          <small style={{ color: '#666', fontSize: '0.875rem' }}>
            10% of bond amount (minimum $100)
          </small>
        </div>
        <div className="form-group">
          <label>Premium Paid Today</label>
          <input
            type="number"
            step="0.01"
            value={formData.bond.premiumPaid}
            onChange={(e) => handleInputChange('bond', 'premiumPaid', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Select Power Certificate *</label>
        {loadingPowers ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
            Loading available powers...
          </div>
        ) : availablePowers.length > 0 ? (
          <>
            <select
              value={formData.bond.powerId}
              onChange={(e) => handleInputChange('bond', 'powerId', e.target.value)}
              required
            >
              <option value="">Select an available power...</option>
              {availablePowers.map(power => (
                <option key={power.id} value={power.id}>
                  {power.power_number} (Limit: ${power.power_limit.toLocaleString()}) - Expires: {new Date(power.expiration_date).toLocaleDateString()}
                </option>
              ))}
            </select>
            <small style={{ color: '#28a745', fontSize: '0.875rem', display: 'block', marginTop: '0.5rem' }}>
              {availablePowers.length} eligible power(s) available for this bond amount
            </small>
          </>
        ) : formData.bond.bondAmount ? (
          <div style={{ padding: '1rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}>
            No powers available for ${parseFloat(formData.bond.bondAmount).toLocaleString()} bond amount.
            Please check your power inventory.
          </div>
        ) : (
          <div style={{ padding: '1rem', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', color: '#666' }}>
            Enter a bond amount above to see available powers
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Collateral Type *</label>
          <select
            value={formData.bond.collateralType}
            onChange={(e) => handleInputChange('bond', 'collateralType', e.target.value)}
            required
          >
            <option value="">Select Collateral Type...</option>
            <option value="indemnity_agreement">Indemnity Agreement</option>
            <option value="real_property">Real Property</option>
            <option value="cash">Cash</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label>Assigned Agent</label>
          <select
            value={formData.agentId}
            onChange={(e) => handleAgentChange(e.target.value)}
          >
            <option value="">Select Agent...</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>
                {agent.first_name} {agent.last_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {formData.bond.collateralType === 'other' && (
        <div className="form-group">
          <label>Specify Other Collateral Type *</label>
          <input
            type="text"
            value={formData.bond.otherCollateralType}
            onChange={(e) => handleInputChange('bond', 'otherCollateralType', e.target.value)}
            placeholder="Enter collateral type"
            required
          />
        </div>
      )}

      <div className="form-group">
        <label>Collateral Description</label>
        <textarea
          rows="3"
          value={formData.bond.collateralDescription}
          onChange={(e) => handleInputChange('bond', 'collateralDescription', e.target.value)}
          placeholder="Describe any collateral securing this bond..."
        ></textarea>
      </div>
    </div>
  );

  const renderCourtForm = () => (
    <div className="form-section">
      <h2><FaGavel /> Court & Case Information</h2>

      <div className="form-row">
        <div className="form-group">
          <label>Court</label>
          <select
            value={formData.court.courtId}
            onChange={(e) => handleInputChange('court', 'courtId', e.target.value)}
          >
            <option value="">Select Court...</option>
            {courts.map(court => (
              <option key={court.id} value={court.id}>
                {court.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Case Number</label>
          <input
            type="text"
            value={formData.court.caseNumber}
            onChange={(e) => handleInputChange('court', 'caseNumber', e.target.value)}
          />
        </div>
      </div>

      <ChargeSelector
        selectedCharges={formData.court.selectedCharges}
        onChange={(charges) => handleInputChange('court', 'selectedCharges', charges)}
      />

      <div className="form-row">
        <div className="form-group">
          <label>Arrest Date</label>
          <input
            type="date"
            value={formData.court.arrestDate}
            onChange={(e) => handleInputChange('court', 'arrestDate', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Booking Number</label>
          <input
            type="text"
            value={formData.court.bookingNumber}
            onChange={(e) => handleInputChange('court', 'bookingNumber', e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Next Court Date</label>
          <input
            type="date"
            value={formData.court.nextCourtDate}
            onChange={(e) => handleInputChange('court', 'nextCourtDate', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Court Time</label>
          <input
            type="time"
            value={formData.court.courtTime}
            onChange={(e) => handleInputChange('court', 'courtTime', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Hearing Type</label>
          <select
            value={formData.court.hearingType}
            onChange={(e) => handleInputChange('court', 'hearingType', e.target.value)}
          >
            <option value="arraignment">Arraignment</option>
            <option value="preliminary">Preliminary Hearing</option>
            <option value="trial">Trial</option>
            <option value="sentencing">Sentencing</option>
            <option value="status">Status Conference</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Judge Name</label>
        <input
          type="text"
          value={formData.court.judgeName}
          onChange={(e) => handleInputChange('court', 'judgeName', e.target.value)}
        />
      </div>
    </div>
  );

  const renderCosignerForm = () => (
    <div className="form-section">
      <h2><FaUserFriends /> Cosigner / Indemnitor Information</h2>
      <p className="form-note">Optional - Add cosigner/indemnitor details if applicable</p>

      <div className="form-row">
        <div className="form-group">
          <label>First Name</label>
          <input
            type="text"
            value={formData.cosigner.firstName}
            onChange={(e) => handleInputChange('cosigner', 'firstName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Last Name</label>
          <input
            type="text"
            value={formData.cosigner.lastName}
            onChange={(e) => handleInputChange('cosigner', 'lastName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Relationship</label>
          <input
            type="text"
            placeholder="e.g., Spouse, Parent, Friend"
            value={formData.cosigner.relationship}
            onChange={(e) => handleInputChange('cosigner', 'relationship', e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={formData.cosigner.phone}
            onChange={(e) => handleInputChange('cosigner', 'phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={formData.cosigner.email}
            onChange={(e) => handleInputChange('cosigner', 'email', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Street Address</label>
        <AddressAutocomplete
          value={formData.cosigner.addressStreet}
          onChange={(value) => handleInputChange('cosigner', 'addressStreet', value)}
          onAddressSelect={handleCosignerAddressSelect}
          placeholder="Start typing address..."
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            value={formData.cosigner.addressCity}
            onChange={(e) => handleInputChange('cosigner', 'addressCity', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>State</label>
          <input
            type="text"
            maxLength="2"
            value={formData.cosigner.addressState}
            onChange={(e) => handleInputChange('cosigner', 'addressState', e.target.value.toUpperCase())}
          />
        </div>
        <div className="form-group">
          <label>ZIP Code</label>
          <input
            type="text"
            maxLength="10"
            value={formData.cosigner.addressZip}
            onChange={(e) => handleInputChange('cosigner', 'addressZip', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="new-bond-container">
      <div className="new-bond-header">
        <h1>Start a New Bond</h1>
        <p>Complete all required information to create a new bail bond</p>
      </div>

      {renderStepIndicator()}

      <form onSubmit={handleSubmit} className="bond-form">
        {currentStep === 1 && renderDefendantForm()}
        {currentStep === 2 && renderBondForm()}
        {currentStep === 3 && renderCourtForm()}
        {currentStep === 4 && renderCosignerForm()}

        <div className="form-actions">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="btn btn-secondary"
            >
              <FaArrowLeft /> Previous
            </button>
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="btn btn-primary"
            >
              Next <FaArrowRight />
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-success"
              disabled={loading}
            >
              <FaSave /> {loading ? 'Creating Bond...' : 'Create Bond'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default NewBond;
